import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Card,
	Modal,
	Segmented,
	Flex,
	Col,
	Row,
	Splitter,
	Input,
	Button,
	Typography,
	Tag,
} from 'antd';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ClearOutlined } from '@ant-design/icons';
import {
	useGetAllMaterialLengthsQuery,
	useGetMaterialLengthsTotalValueQuery,
	useDeleteMaterialLengthMutation,
	useRecordAttributionCostMutation,
	useGetMaterialShapesQuery,
	useGetMaterialCategoriesQuery,
	useGetMaterialTypesQuery,
	useGetInventoryLocationsQuery,
	useGetMaterialShelvesQuery,
	useGetMaterialLengthDimensionRangesQuery,
} from '../../redux/api/material';
import MaterialLengthsTable from './MaterialLengthsTable';
import AppliedFiltersBar from './AppliedFiltersBar';
import MaterialReservationModal from '../MaterialReservationModal';
import ModifyLengthsModal from './ModifyLengthsModal';
import MoveMaterialModal from './MoveMaterialModal';
import type { MoveMaterialSummaryData } from './MoveMaterialModal';
import MaterialLocationsSummaryModal from '../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import SplitLengthModal from '../MaterialInventory/SplitLengthModal';
import Header from '../Header';
import { MaterialLengthsFilter } from './filters';
import { useGetBuildingsQuery } from '../../redux/api/location';

// ─── Query param keys that the API accepts ──────────────────────────────────
// Single source of truth: if you add a new filter, add its URL key(s) here.
const FILTER_PARAM_KEYS = [
	'search',
	'shape',
	'category',
	'types',
	'inventory_location',
	'building',
	'status',
	'has_certificate',
	'min_total_available_length',
	'max_total_available_length',
	'current_length_min',
	'current_length_max',
	'min_reservations',
	'max_reservations',
	'purchase_order_number',
	'length_friendly_id',
	'height_min',
	'height_max',
	'width_min',
	'width_max',
	'outer_diameter_min',
	'outer_diameter_max',
	'inner_diameter_min',
	'inner_diameter_max',
	'height_od_min',
	'height_od_max',
	'width_id_min',
	'width_id_max',
	'wall_min',
	'wall_max',
	'sort_by',
	'ordering',
] as const;

/**
 * Builds the API query object from URL search params.
 * Reads every known filter key and includes it only if it has a value.
 */
function buildApiQuery(
	searchParams: URLSearchParams,
	pageSize: number
): Record<string, string | number> {
	const query: Record<string, string | number> = {
		page: parseInt(searchParams.get('page') || '1', 10),
		page_size: pageSize,
	};
	const hasIndependentDimensionFilter = [
		'height_min',
		'height_max',
		'width_min',
		'width_max',
		'outer_diameter_min',
		'outer_diameter_max',
		'inner_diameter_min',
		'inner_diameter_max',
	].some((key) => searchParams.get(key));
	const legacyCombinedDimensionKeys = [
		'height_od_min',
		'height_od_max',
		'width_id_min',
		'width_id_max',
	];

	for (const key of FILTER_PARAM_KEYS) {
		if (hasIndependentDimensionFilter && legacyCombinedDimensionKeys.includes(key)) {
			continue;
		}
		const value = searchParams.get(key);
		if (value) {
			query[key] = value;
		}
	}

	return query;
}

/**
 * Builds the dimension range query params from URL search params.
 * Only includes filters that define "context" (shape, category, types, etc.).
 * Excludes dimension range params (width_id_min/max, height_od_min/max, etc.)
 * so the API returns the full min/max for that context; the slider can then
 * always use the full range and the user can adjust without reset.
 */
function buildRangeQueryParams(
	searchParams: URLSearchParams
): Record<string, string> {
	const RANGE_AFFECTING_KEYS = [
		'shape',
		'category',
		'types',
		'inventory_location',
		'status',
		'has_certificate',
		'purchase_order_number',
	];

	const params: Record<string, string> = {};
	for (const key of RANGE_AFFECTING_KEYS) {
		const value = searchParams.get(key);
		if (value) params[key] = value;
	}
	return params;
}

// ─── Page Component ──────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const MaterialLengthsPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// ── Shape data ───────────────────────────────────────────────────────────
	const shapesQuery = useGetMaterialShapesQuery();

	// ── Derived values from URL ──────────────────────────────────────────────
	const currentPage = parseInt(searchParams.get('page') || '1', 10);
	const category = searchParams.get('category') || '';
	const search = searchParams.get('search') || '';

	// ── API Queries ──────────────────────────────────────────────────────────
	const apiQuery = buildApiQuery(searchParams, PAGE_SIZE);

	const { data, isLoading, isFetching, error, refetch } =
		useGetAllMaterialLengthsQuery(apiQuery, {
			refetchOnMountOrArgChange: true,
		});

	const { data: totalValueData } = useGetMaterialLengthsTotalValueQuery(
		apiQuery,
		{ refetchOnMountOrArgChange: true }
	);

	const [deleteMaterialLength] = useDeleteMaterialLengthMutation();
	const [recordAttributionCost] = useRecordAttributionCostMutation();

	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: category },
		{
			skip: !categoriesQuery.isSuccess || !category,
			refetchOnMountOrArgChange: true,
		}
	);
	const locationsQuery = useGetInventoryLocationsQuery({});
	const shelvesQuery = useGetMaterialShelvesQuery();
	const { data: buildings = [] } = useGetBuildingsQuery();

	const rangeQueryParams = useMemo(
		() => buildRangeQueryParams(searchParams),
		[searchParams]
	);

	const { data: dimensionRanges } = useGetMaterialLengthDimensionRangesQuery(
		rangeQueryParams,
		{ refetchOnMountOrArgChange: true }
	);

	// ── Local UI state ───────────────────────────────────────────────────────
	const [searchValue, setSearchValue] = useState(search);
	const [isMaterialReservationModalOpen, setIsMaterialReservationModalOpen] =
		useState(false);
	const [selectedMaterialLength, setSelectedMaterialLength] =
		useState<any>(null);
	const [isSplitLengthModalOpen, setIsSplitLengthModalOpen] = useState(false);

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [isModifyLengthsModalOpen, setIsModifyLengthsModalOpen] =
		useState(false);
	const [isMoveMaterialModalOpen, setIsMoveMaterialModalOpen] = useState(false);
	const [moveSummaryData, setMoveSummaryData] =
		useState<MoveMaterialSummaryData | null>(null);
	const [showValueSummary, setShowValueSummary] = useState(() => {
		const saved = sessionStorage.getItem('showValueSummary');
		return saved !== null ? saved === 'true' : true;
	});

	useEffect(() => {
		sessionStorage.setItem('showValueSummary', String(showValueSummary));
	}, [showValueSummary]);

	// Sync search input when URL changes (e.g. browser back)
	useEffect(() => {
		setSearchValue(search);
	}, [search]);

	// ── Handlers ─────────────────────────────────────────────────────────────
	const handlePageChange = (newPage: number) => {
		searchParams.set('page', newPage.toString());
		navigate({ search: searchParams.toString() });
	};

	const handleSearch = (value: string) => {
		if (value.trim()) {
			searchParams.set('search', value.trim());
		} else {
			searchParams.delete('search');
		}
		searchParams.set('page', '1');
		navigate({ search: searchParams.toString() });
	};

	const handleReserve = (record: any) => {
		setSelectedMaterialLength({
			...record,
			material: {
				id: record.material_id,
				total_available_length: record.material_total_available_length,
			},
		});
		setIsMaterialReservationModalOpen(true);
	};

	const handleDelete = (record: any) => {
		Modal.confirm({
			title: 'Delete Material Length?',
			content: `Are you sure you want to delete material length ${record.friendly_id}?`,
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					await deleteMaterialLength({ id: record.id }).unwrap();
					refetch();
				} catch (err) {
					console.error('Failed to delete:', err);
				}
			},
		});
	};

	const handleDeleteSelected = () => {
		const withReservations = selectedRows.filter(
			(row) => (row.material_reservations || []).length > 0
		);
		const deletable = selectedRows.filter(
			(row) => (row.material_reservations || []).length === 0
		);

		if (deletable.length === 0) {
			Modal.warning({
				title: 'Cannot Delete',
				content:
					'All selected lengths have reservations and cannot be deleted.',
				okButtonProps: {
					style: {
						backgroundColor: 'transparent',
						borderColor: '#000',
						color: '#000',
					},
				},
			});
			return;
		}

		const hasSkipped = withReservations.length > 0;

		Modal.confirm({
			title: `Delete ${deletable.length} Material Length${
				deletable.length !== 1 ? 's' : ''
			}?`,
			content: (
				<div>
					{hasSkipped && (
						<Typography.Text
							type="warning"
							style={{ display: 'block', marginBottom: 8 }}
						>
							{withReservations.length} length
							{withReservations.length !== 1 ? 's' : ''} with active
							reservations will be skipped.
						</Typography.Text>
					)}
					<span>
						Are you sure you want to delete {deletable.length} material length
						{deletable.length !== 1 ? 's' : ''}?
					</span>
				</div>
			),
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					await Promise.all(
						deletable.map((row) =>
							deleteMaterialLength({ id: row.id }).unwrap()
						)
					);
					setSelectedRowKeys([]);
					setSelectedRows([]);
					await refetch();
				} catch (err) {
					console.error('Failed to delete:', err);
				}
			},
		});
	};

	const handleSplit = (record: any) => {
		setSelectedMaterialLength(record);
		setIsSplitLengthModalOpen(true);
	};

	const handleCloseReservationModal = () => {
		setIsMaterialReservationModalOpen(false);
		setSelectedMaterialLength(null);
	};

	const handleCloseSplitModal = () => {
		setIsSplitLengthModalOpen(false);
		setSelectedMaterialLength(null);
	};

	const handleSelectionChange = (keys: React.Key[], _newRows: any[]) => {
		void _newRows;
		setSelectedRowKeys(keys);
		setSelectedRows((prevRows) => {
			const kept = prevRows.filter((r) => keys.includes(r.id));
			const keptIds = new Set(kept.map((r) => r.id));
			const added = (data?.results || []).filter(
				(r: any) => keys.includes(r.id) && !keptIds.has(r.id)
			);
			return [...kept, ...added];
		});
	};

	const handleRemoveSelectedLength = useCallback(
		(id: string) => {
			const target = String(id);
			setSelectedRowKeys((prev) => {
				const next = prev.filter((k) => String(k) !== target);
				if (next.length === 0) {
					setIsModifyLengthsModalOpen(false);
					setIsMoveMaterialModalOpen(false);
				}
				return next;
			});
			setSelectedRows((prev) => prev.filter((r) => String(r.id) !== target));
		},
		[]
	);

	const handleOpenModifyLengthsModal = () => {
		setIsModifyLengthsModalOpen(true);
	};

	const handleCloseModifyLengthsModal = () => {
		setIsModifyLengthsModalOpen(false);
	};

	const handleOpenMoveMaterialModal = () => {
		setIsMoveMaterialModalOpen(true);
	};

	const handleCloseMoveMaterialModal = () => {
		setIsMoveMaterialModalOpen(false);
	};

	// Derive selected lengths from current table data so modal always gets fresh current_length after refetch
	const selectedLengthsForModal = useMemo(() => {
		return selectedRows.filter((r) => selectedRowKeys.includes(r.id));
	}, [selectedRows, selectedRowKeys]);

	// Lock selection to a single building: once a row is selected, only rows
	// from the same building can be added to the selection.
	const selectedBuildingId = useMemo(() => {
		if (selectedRows.length === 0) return null;
		const first = selectedRows.find((r) => selectedRowKeys.includes(r.id));
		return first?.inventory_location?.shelf?.rack?.building?.id ?? null;
	}, [selectedRows, selectedRowKeys]);

	// ── Total value for filtered results (from API, independent of selection) ─
	const totalValue = totalValueData?.total_value != null
		? parseFloat(totalValueData.total_value)
		: 0;
	const totalValueCount = totalValueData?.count ?? 0;

	// ── Error state ──────────────────────────────────────────────────────────
	if (error) {
		return (
			<Card>
				<div>Error loading material lengths</div>
				<pre>{JSON.stringify(error, null, 2)}</pre>
			</Card>
		);
	}

	// ── Render ───────────────────────────────────────────────────────────────
	return (
		<>
			<Flex justify="space-between" align="center" className="mb-2">
				<Header
					title="Material Lengths"
					breadcrumbItems={[
						{ title: <Link to="/">Home</Link> },
						{ title: 'Material Lengths' },
					]}
				/>
				<Flex justify="flex-end" align="end" className="gap-2" vertical>
					<Segmented
						options={[
							{ label: 'Material', value: 'material' },
							{ label: 'Lengths', value: 'lengths' },
						]}
						value="lengths"
						onChange={(value) => {
							if (value === 'material') {
								navigate('/material/materials');
							}
						}}
					/>
					<Flex align="center" gap={12}>
						{/* Sum of value: visible cuando "Show Value" está activado en el panel izquierdo */}
						{showValueSummary && (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									padding: '4px 12px',
									background: '#f6ffed',
									border: '1px solid #b7eb8f',
									borderRadius: 6,
									height: 32,
								}}
							>
								<Typography.Text type="secondary" style={{ fontSize: 13 }}>
									Total value (filtered)
									{totalValueCount > 0 ? ` · ${totalValueCount} items` : ''}
								</Typography.Text>
								<Typography.Text
									strong
									style={{ fontSize: 14, color: '#389e0d' }}
								>
									$
									{totalValue.toLocaleString('en-US', {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}
								</Typography.Text>
							</div>
						)}
						<Button
							danger
							onClick={handleDeleteSelected}
							disabled={selectedRowKeys.length === 0}
						>
							Delete Selected
						</Button>
						<Button
							type="primary"
							onClick={handleOpenModifyLengthsModal}
							disabled={selectedRowKeys.length === 0}
						>
							Modify Material
						</Button>
						<Button
							onClick={handleOpenMoveMaterialModal}
							disabled={selectedRowKeys.length === 0}
						>
							Move
						</Button>
						{selectedRowKeys.length > 0 && (
							<Flex align="center" gap={8}>
								<Tag color="blue">
									{selectedRowKeys.length} selected
								</Tag>
								<Button
									type="link"
									size="small"
									icon={<ClearOutlined />}
									style={{ padding: 0, height: 'auto' }}
									onClick={() => {
										setSelectedRowKeys([]);
										setSelectedRows([]);
									}}
								>
									Clear
								</Button>
							</Flex>
						)}
					</Flex>
				</Flex>
			</Flex>

			<Row>
				<Col span={24}>
					<Splitter
						style={{
							width: '100%',
							height: 'calc(100vh - 160px)',
							background: 'transparent',
						}}
					>
						{/* ── Left Panel: Search + Filters ──────────────────────── */}
						<Splitter.Panel
							defaultSize={280}
							min={200}
							collapsible
							style={{
								overflowY: 'auto',
								padding: '0 5px 0 0',
								display: 'flex',
								flexDirection: 'column',
								gap: 16,
								background: 'transparent',
							}}
						>
							<Input.Search
								placeholder="Search ID, location, PO"
								allowClear
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
								onSearch={handleSearch}
							/>

							<MaterialLengthsFilter
								layout="desktop"
								shapes={shapesQuery.data || []}
								categories={categoriesQuery.data || []}
								types={typesQuery.data || []}
								locations={locationsQuery.data?.results || []}
								buildings={buildings}
								queryParams={searchParams}
								navigate={navigate}
								heightRange={dimensionRanges?.height}
								widthRange={dimensionRanges?.width}
								outerDiameterRange={dimensionRanges?.outer_diameter}
								innerDiameterRange={dimensionRanges?.inner_diameter}
								wallThicknessRange={dimensionRanges?.wall}
								currentLengthRange={dimensionRanges?.current_length}
								showValueSummary={showValueSummary}
								onToggleValueSummary={setShowValueSummary}
							/>
						</Splitter.Panel>

						{/* ── Right Panel: Table ───────────────────────────────── */}
						<Splitter.Panel
							style={{
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden',
								background: '#fff',
							}}
						>
							<AppliedFiltersBar
								queryParams={searchParams}
								navigate={(opts) => navigate({ search: opts.search })}
								shapes={shapesQuery.data || []}
								categories={categoriesQuery.data || []}
								types={typesQuery.data || []}
								buildings={buildings}
								locations={locationsQuery.data?.results || []}
								dimensionRanges={dimensionRanges}
								style={{ margin: '0 0 8px' }}
							/>
							<MaterialLengthsTable
								data={data?.results || []}
								loading={isLoading || isFetching}
								selectedRowKeys={selectedRowKeys}
								onSelectionChange={handleSelectionChange}
								selectedBuildingId={selectedBuildingId}
								pagination={{
									current: currentPage,
									pageSize: PAGE_SIZE,
									total: data?.count || 0,
									onChange: handlePageChange,
								}}
								onReserve={handleReserve}
								onMove={(record: any) => console.log('Move:', record)}
								onDelete={handleDelete}
								onSplit={handleSplit}
								queryParams={searchParams}
								navigate={navigate}
								locations={locationsQuery.data?.results || []}
								shapes={shapesQuery.data || []}
								categories={categoriesQuery.data || []}
								types={typesQuery.data || []}
								buildings={buildings}
								dimensionRanges={dimensionRanges}
							/>
						</Splitter.Panel>
					</Splitter>
				</Col>
			</Row>

			{/* ── Modals ──────────────────────────────────────────────────── */}
			{isMaterialReservationModalOpen && selectedMaterialLength && (
				<MaterialReservationModal
					onClose={handleCloseReservationModal}
					selectedMaterialReservation={null}
					selectedMaterial={selectedMaterialLength.material}
					selectedMaterialLength={selectedMaterialLength}
					isMaterialReservationModalOpen={isMaterialReservationModalOpen}
					reserveMultipleLengths={false}
					refetchData={refetch}
				/>
			)}

			<ModifyLengthsModal
				open={isModifyLengthsModalOpen}
				onClose={handleCloseModifyLengthsModal}
				selectedLengths={selectedLengthsForModal}
				onRemoveLength={handleRemoveSelectedLength}
				onConfirm={async (operatorId, modifications) => {
					try {
						await recordAttributionCost({
							operator_id: String(operatorId),
							modifications,
						}).unwrap();
						refetch();
					} catch (err) {
						console.error('Failed to record attribution cost:', err);
					}
				}}
			/>

			<MoveMaterialModal
				open={isMoveMaterialModalOpen}
				onClose={handleCloseMoveMaterialModal}
				selectedLengths={selectedLengthsForModal}
				shelves={shelvesQuery.data}
				onRemoveLength={handleRemoveSelectedLength}
				onConfirmSuccess={(data) => setMoveSummaryData(data)}
				refetch={refetch}
			/>

			{moveSummaryData && (
				<MaterialLocationsSummaryModal
					visible={true}
					onComplete={() => setMoveSummaryData(null)}
					materialInfo={moveSummaryData.materialInfo}
					lengths={moveSummaryData.lengths}
					title="Material Locations Summary"
				/>
			)}

			{isSplitLengthModalOpen && selectedMaterialLength && (
				<SplitLengthModal
					isSplitLengthModalOpen={isSplitLengthModalOpen}
					onClose={handleCloseSplitModal}
					material={
						{
							id: selectedMaterialLength.material_id,
							display_name: selectedMaterialLength.material_display_name,
							type: selectedMaterialLength.material_type,
							shape: selectedMaterialLength.material_shape,
							width: selectedMaterialLength.width,
							height: selectedMaterialLength.height,
							outer_diameter: selectedMaterialLength.outer_diameter,
							inner_diameter: selectedMaterialLength.inner_diameter,
							wall_thickness: selectedMaterialLength.wall_thickness,
							total_available_length:
								selectedMaterialLength.material_total_available_length,
						} as any
					}
					selectedMaterialLength={selectedMaterialLength}
					refetchData={refetch}
				/>
			)}
		</>
	);
};

export default MaterialLengthsPage;
